// components/SettingsModal.tsx
// アプリ全体の設定をまとめるモーダル。現状は「解析」セクションに AI解析モード選択を持つ。

import React, { useEffect, useState } from 'react';
import { Modal, Radio, Space, Typography, Divider } from 'antd';
import {
  OcrModel,
  OCR_MODEL_LABELS,
  OCR_MODEL_DESCRIPTIONS,
  getOcrModelPreference,
  setOcrModelPreference,
} from '../utils/ocrModel';
import { isUseChatGpt } from '../utils/chatGptOcr';

const { Text, Title } = Typography;

export interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
  zIndex?: number;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ open, onClose, zIndex }) => {
  const [ocrModel, setOcrModel] = useState<OcrModel>('high');
  const [loaded, setLoaded] = useState<boolean>(false);
  // 緊急モード（ComSystemMapper の UseChatGPT が true）。true の間は解析設定を変更不可にする。
  const [emergencyMode, setEmergencyMode] = useState<boolean>(false);

  // モーダルを開いたタイミングで現在の設定値・緊急モードを読み込む
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const [saved, useChatGpt] = await Promise.all([
        getOcrModelPreference(),
        isUseChatGpt(),
      ]);
      if (!cancelled) {
        setOcrModel(saved);
        setEmergencyMode(useChatGpt);
        setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleOcrModelChange = async (value: OcrModel) => {
    setOcrModel(value);
    await setOcrModelPreference(value);
  };

  const renderOcrOption = (value: OcrModel) => (
    <Radio value={value} style={{ alignItems: 'flex-start' }}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Text strong style={{ fontSize: 15 }}>{OCR_MODEL_LABELS[value]}</Text>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {OCR_MODEL_DESCRIPTIONS[value]}
        </Text>
      </div>
    </Radio>
  );

  return (
    <Modal
      title="設定"
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      zIndex={zIndex}
      destroyOnClose
    >
      {/* 解析セクション */}
      <Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>解析</Title>
      <Text type="secondary" style={{ fontSize: 13 }}>名刺の AI 解析モードを選択します</Text>
      {emergencyMode && (
        <div style={{ marginTop: 8 }}>
          <Text style={{ color: '#cf1322', fontSize: 13 }}>
            現在緊急モードの為、選択できません。
          </Text>
        </div>
      )}
      <div style={{ marginTop: 12 }}>
        <Radio.Group
          value={ocrModel}
          onChange={e => handleOcrModelChange(e.target.value as OcrModel)}
          style={{ width: '100%' }}
          disabled={!loaded || emergencyMode}
        >
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {renderOcrOption('high')}
            {renderOcrOption('fast')}
          </Space>
        </Radio.Group>
      </div>

      <Divider style={{ margin: '24px 0 0' }} />
    </Modal>
  );
};

export default SettingsModal;
